import dayjs from "dayjs";
import type { PlayerCardInjury, PlayerCardStatusEntry } from "../Processing/types";

const NO_DATA_STATUS_ID = 9999999;
const NO_DATA_COLOR = "#f2f2f2";

const SIDE_ABBREV: Record<string, string> = { left: "L", right: "R", both: "LR" };

/** Primary injury label = body location with the side abbreviated, e.g. "Knee (L)". */
export function injuryName(injury: PlayerCardInjury): string {
  return injury.injury_location.replace(
    /\((Left|Right|Both)\)/gi,
    (_m, side: string) => `(${SIDE_ABBREV[side.toLowerCase()]})`,
  );
}

/** Active-injury recency, always relative, e.g. "260 days ago" / "1 day ago". */
export function formatActiveAge(injury: PlayerCardInjury): string {
  const days = dayjs().startOf("day").diff(dayjs(injury.injury_start_date).startOf("day"), "day");
  return `${days} ${days === 1 ? "day" : "days"} ago`;
}

export interface InjuryRangeParts {
  start: string;
  end: string; // "" when the injury is still open
}

/**
 * Recent-injury date range pieces, no year, zero-padded day (e.g. "Oct 19", "Jan 06").
 * Returned as separate start/end so the view can column-align them; `end` is "" for
 * an open injury.
 */
export function injuryRangeParts(injury: PlayerCardInjury): InjuryRangeParts {
  return {
    start: dayjs(injury.injury_start_date).format("MMM DD"),
    end: injury.injury_end_date ? dayjs(injury.injury_end_date).format("MMM DD") : "",
  };
}

export interface AvailabilityCell {
  date: string;
  color: string;
  name: string;
  label: string; // "MMM D" for the tooltip
}

/**
 * Sort last_5_status by ISO date ascending. Empty (`{}`) or sentinel entries
 * default to "No Data" (grey), with the date taken from the map key.
 */
export function sortAvailabilityEntries(
  last5: Record<string, PlayerCardStatusEntry>,
): AvailabilityCell[] {
  return Object.keys(last5)
    .sort() // ISO date strings sort chronologically
    .map((date) => {
      const entry = last5[date] as Partial<PlayerCardStatusEntry> | undefined;
      const isNoData = !entry || entry.status_id === NO_DATA_STATUS_ID || !entry.color;
      return {
        date,
        color: isNoData ? NO_DATA_COLOR : (entry!.color as string),
        name: isNoData ? "No Data" : (entry!.name as string),
        label: dayjs(date).format("MMM D"),
      };
    });
}
